import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const ROLE_LEVELS = {
  SuperAdmin: 1,
  Admin: 1,
  Manager: 2,
  'Assistant Manager': 2,
  Supervisor: 3,
  'Store Supervisor': 3,
  Staff: 4,
};

const FEATURE_ROLE_FALLBACKS = {
  'tasks.board': ['Staff', 'Supervisor'],
  'dairy_order.task': ['Staff', 'Supervisor'],
  'dairy_order.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'goods_received.task': ['Staff', 'Supervisor', 'Assistant Manager'],
  'goods_received.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'product_returns.task': ['Staff', 'Supervisor'],
  'product_returns.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'item_change_log.page': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor', 'Staff'],
  'produce_tracker.task': ['Staff', 'Supervisor'],
  'produce_tracker.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'inventory_count.task': ['Staff', 'Supervisor'],
  'inventory_count.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'daily_liquor_count.task': ['Staff', 'Supervisor'],
  'daily_liquor_count.report': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'final_report.view': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'workforce_roster.view': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor', 'Staff'],
  'workforce_roster.manage': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'reports.hub': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'reports.all_stores': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.hub': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.overview': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.funnel': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.promo': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.calendar': ['Admin', 'Manager', 'Assistant Manager'],
  'bi.ba_report': ['Admin', 'Manager', 'Assistant Manager'],
  'inventory.daily': ['Admin', 'Manager', 'Assistant Manager', 'Supervisor'],
  'users.manage': ['SuperAdmin', 'Admin'],
  'admin.feature_access': ['SuperAdmin', 'Admin'],
  'admin.category_classifications': ['SuperAdmin', 'Admin'],
  'admin.assets': ['SuperAdmin', 'Admin'],
  'products.sync': ['SuperAdmin', 'Admin', 'Manager'],
  'notifications.admin': ['SuperAdmin', 'Admin'],
  'dashboard.admin_overview': ['SuperAdmin', 'Admin', 'Manager'],
  'stores.manage': ['SuperAdmin', 'Admin', 'Manager'],
  'store_close.close': ['SuperAdmin', 'Admin', 'Manager', 'Assistant Manager', 'Supervisor', 'Staff'],
  'store_close.edit': ['SuperAdmin', 'Admin'],
};

const FEATURE_KEY_ALIASES = {
  'logistics.tracker': ['logistics.manage', 'logistics.desk'],
  'logistics.desk': ['logistics.tracker'],
  'logistics.manage': ['logistics.desk'],
};

const normalizeString = (value) => String(value || '').trim().toLowerCase();
const normalizePermissionList = (permissions) => (Array.isArray(permissions) ? permissions : []);
const ROLE_LEVELS_NORMALIZED = Object.fromEntries(
  Object.entries(ROLE_LEVELS).map(([role, level]) => [normalizeString(role), level])
);

const hasActionPermission = (actions, expectedAction) => {
  const normalizedAction = normalizeString(expectedAction);
  const normalizedActions = Array.isArray(actions)
    ? actions.map((action) => normalizeString(action))
    : [];

  return normalizedActions.some((action) => {
    if (!action) return false;
    if (action === '*' || action === 'all') return true;
    if (action === normalizedAction) return true;

    if (action.endsWith('.*')) {
      const prefix = action.slice(0, -2);
      return normalizedAction.startsWith(`${prefix}.`);
    }

    if (action.endsWith(':*')) {
      const prefix = action.slice(0, -2);
      return normalizedAction.startsWith(`${prefix}:`);
    }

    return false;
  });
};

const getFeaturePermissionEntry = (permissions = []) => (
  normalizePermissionList(permissions).find(
    (entry) => normalizeString(entry?.module) === 'feature_access'
  )
);

const getFeatureCandidates = (featureKey) => {
  const normalizedFeature = normalizeString(featureKey);
  const aliases = FEATURE_KEY_ALIASES[normalizedFeature] || [];
  return Array.from(new Set([normalizedFeature, ...aliases.map((entry) => normalizeString(entry))]));
};

const hasFeatureGrant = (permissions = [], featureKey = '') => {
  const candidates = getFeatureCandidates(featureKey);
  const featurePermission = getFeaturePermissionEntry(permissions);
  if (!featurePermission) return false;
  return candidates.some((candidate) => hasActionPermission(featurePermission.actions, candidate));
};

const getUserFeatureAccessConfig = (user) => {
  const rawConfig = user?.featureAccess
    || user?.feature_access
    || user?.performanceMetrics?.featureAccess
    || user?.performanceMetrics?.feature_access
    || user?.performance_metrics?.featureAccess
    || user?.performance_metrics?.feature_access
    || null;

  if (!rawConfig) {
    return { hasExplicitConfig: false, actions: [] };
  }

  if (Array.isArray(rawConfig)) {
    return { hasExplicitConfig: true, actions: rawConfig };
  }

  if (typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
    const mode = normalizeString(rawConfig.mode || '');
    if (mode === 'inherit') {
      return { hasExplicitConfig: false, actions: [] };
    }

    const actions = Array.isArray(rawConfig.actions) ? rawConfig.actions : [];
    return {
      hasExplicitConfig: mode === 'custom' || actions.length > 0,
      actions,
    };
  }

  return { hasExplicitConfig: false, actions: [] };
};

const isAllowedByRoleFallback = (user, allowedRoles = []) => {
  if (!user || !Array.isArray(allowedRoles) || !allowedRoles.length) return false;
  const roleName = normalizeString(user?.role);
  if (allowedRoles.map((role) => normalizeString(role)).includes(roleName)) return true;

  const maxAllowedLevel = Math.max(
    ...allowedRoles
      .map((allowedRole) => ROLE_LEVELS[allowedRole])
      .filter((level) => Number.isFinite(level))
  );

  if (!Number.isFinite(maxAllowedLevel)) return false;
  return getRoleLevel(user) <= maxAllowedLevel;
};

const getRoleLevel = (user) => {
  const explicitLevel = Number(user?.roleHierarchyLevel ?? user?.roles?.hierarchy_level);
  if (Number.isFinite(explicitLevel) && explicitLevel > 0) {
    return explicitLevel;
  }

  return ROLE_LEVELS_NORMALIZED[normalizeString(user?.role)] || Number.POSITIVE_INFINITY;
};

const normalizeAuthUser = (user) => {
  if (!user || typeof user !== 'object') return user;

  const nextUser = { ...user };
  if (nextUser.id == null && nextUser._id != null) {
    nextUser.id = nextUser._id;
  }
  if (nextUser._id == null && nextUser.id != null) {
    nextUser._id = nextUser.id;
  }
  if (nextUser.storeId == null && nextUser.store_id != null) {
    nextUser.storeId = nextUser.store_id;
  }
  if (nextUser.store_id == null && nextUser.storeId != null) {
    nextUser.store_id = nextUser.storeId;
  }

  return nextUser;
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      requiresPasswordReset: false,
      
      // Initialize auth state from localStorage token
      initAuth: async () => {
        const token = localStorage.getItem('token');
        const cachedUser = get().user;
        if (!token) {
          set({ isAuthenticated: false, user: null, token: null, requiresPasswordReset: false });
          return false;
        }

        if (cachedUser) {
          const normalizedCachedUser = normalizeAuthUser(cachedUser);
          set({
            user: normalizedCachedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            requiresPasswordReset: Boolean(normalizedCachedUser?.requiresPasswordReset),
          });
        }

        set({ isLoading: true });

        try {
          const response = await authAPI.getMe();
          const { data } = response.data;
          const normalizedUser = normalizeAuthUser(data);

          set({
            user: normalizedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            requiresPasswordReset: Boolean(normalizedUser?.requiresPasswordReset),
          });
          return true;
        } catch (error) {
          localStorage.removeItem('token');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            requiresPasswordReset: false,
          });
          return false;
        }
      },

      login: async (credentials) => {
        set({ isLoading: true });
        try {
          const response = await authAPI.login(credentials);
          const { data } = response.data;
          const normalizedUser = normalizeAuthUser(data);
          
          localStorage.setItem('token', data.token);
          
          set({
            user: normalizedUser,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
            requiresPasswordReset: Boolean(normalizedUser?.requiresPasswordReset),
          });
          
          toast.success('Login successful!');
          return true;
        } catch (error) {
          set({ isLoading: false });
          toast.error(error.response?.data?.message || 'Login failed');
          return false;
        }
      },

      register: async (userData) => {
        set({ isLoading: true });
        try {
          const response = await authAPI.register(userData);
          const { data } = response.data;
          const normalizedUser = normalizeAuthUser(data);
          
          localStorage.setItem('token', data.token);
          
          set({
            user: normalizedUser,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
            requiresPasswordReset: Boolean(normalizedUser?.requiresPasswordReset),
          });
          
          toast.success('Registration successful!');
          return true;
        } catch (error) {
          set({ isLoading: false });
          toast.error(error.response?.data?.message || 'Registration failed');
          return false;
        }
      },

      logout: async () => {
        try {
          await authAPI.logout();
        } catch (error) {
          console.error('Logout error:', error);
        }
        
        localStorage.removeItem('token');
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('store-context-storage');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          requiresPasswordReset: false,
        });
        toast.success('Logged out successfully');
      },

      setRequiresPasswordReset: (value) => {
        const nextValue = Boolean(value);
        set((state) => ({
          requiresPasswordReset: nextValue,
          user: state.user ? { ...state.user, requiresPasswordReset: nextValue } : state.user,
        }));
      },

      hasPermission: (module, action) => {
        const { user } = get();
        const permissions = normalizePermissionList(user?.rolePermissions);

        // Admin has all permissions
        if (getRoleLevel(user) <= 1) return true;

        const normalizedModule = normalizeString(module);
        const normalizedAction = normalizeString(action);
        const userFeatureConfig = getUserFeatureAccessConfig(user);
        if (userFeatureConfig.hasExplicitConfig) {
          if (
            hasActionPermission(userFeatureConfig.actions, 'all')
            || hasActionPermission(userFeatureConfig.actions, normalizedModule)
            || hasActionPermission(userFeatureConfig.actions, `${normalizedModule}:${normalizedAction}`)
          ) {
            return true;
          }
        }

        if (!permissions.length) return false;

        if (
          hasFeatureGrant(permissions, 'all')
          || hasFeatureGrant(permissions, normalizedModule)
          || hasFeatureGrant(permissions, `${normalizedModule}:${normalizedAction}`)
        ) {
          return true;
        }
        
        const modulePermission = permissions.find(
          (permission) => {
            const permissionModule = normalizeString(permission?.module);
            return permissionModule === normalizedModule
              || permissionModule === '*'
              || permissionModule === 'all';
          }
        );
        
        return hasActionPermission(modulePermission?.actions, normalizedAction);
      },

      hasFeatureAccess: (featureKey) => {
        const { user } = get();
        if (!user) return false;

        const normalizedFeature = normalizeString(featureKey);
        const candidates = getFeatureCandidates(normalizedFeature);
        if (
          normalizedFeature === 'item_change_log.page'
          && isAllowedByRoleFallback(user, FEATURE_ROLE_FALLBACKS['item_change_log.page'] || [])
        ) {
          return true;
        }
        if (
          normalizedFeature === 'workforce_roster.view'
          && isAllowedByRoleFallback(user, FEATURE_ROLE_FALLBACKS['workforce_roster.view'] || [])
        ) {
          return true;
        }
        if (
          (
            normalizedFeature === 'bi.hub'
            || normalizedFeature === 'bi.overview'
            || normalizedFeature === 'bi.funnel'
            || normalizedFeature === 'bi.promo'
            || normalizedFeature === 'bi.calendar'
            || normalizedFeature === 'bi.ba_report'
          )
          && isAllowedByRoleFallback(user, FEATURE_ROLE_FALLBACKS[normalizedFeature] || [])
        ) {
          return true;
        }
        const userFeatureConfig = getUserFeatureAccessConfig(user);
        if (userFeatureConfig.hasExplicitConfig) {
          const hasExplicitAccess = hasActionPermission(userFeatureConfig.actions, 'all')
            || candidates.some((candidate) => hasActionPermission(userFeatureConfig.actions, candidate));
          if (hasExplicitAccess) {
            return true;
          }
          for (const candidate of candidates) {
            const fallbackRoles = FEATURE_ROLE_FALLBACKS[candidate];
            if (fallbackRoles?.length && isAllowedByRoleFallback(user, fallbackRoles)) {
              return true;
            }
          }
          return false;
        }

        const permissions = normalizePermissionList(user?.rolePermissions);
        if (permissions.length) {
          const hasRoleFeatureModule = Boolean(getFeaturePermissionEntry(permissions));
          if (hasFeatureGrant(permissions, 'all') || hasFeatureGrant(permissions, normalizedFeature)) {
            return true;
          }

          if (hasRoleFeatureModule) {
            return false;
          }
        }

        for (const candidate of candidates) {
          const fallbackRoles = FEATURE_ROLE_FALLBACKS[candidate];
          if (fallbackRoles?.length && isAllowedByRoleFallback(user, fallbackRoles)) {
            return true;
          }
        }

        return false;
      },

      isAdmin: () => {
        const { user } = get();
        return getRoleLevel(user) <= 1;
      },

      isManager: () => {
        const { user } = get();
        return getRoleLevel(user) <= 2;
      },

      isSupervisor: () => {
        const { user } = get();
        return getRoleLevel(user) <= 3;
      },

      isAssistantManager: () => {
        const { user } = get();
        return user?.role === 'Assistant Manager';
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);
